import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from 'styles/util';
import { required } from 'form/validation';
import strings from 'localization/strings';
import { useAppContext } from 'context/AppContext';

import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Form from 'form/Form';
import { TextField, Checkboxes, Radios } from 'mui-rff';
import Text from 'common/Text';
import IconSendMessage from 'common/IconSendMessage';
import PredefinedTextSelector, { insertPredefinedText } from 'common/PredefinedTextSelector';

const useStyles = makeStyles(({ theme, colors }) => ({
    shortText: {
        fontSize: '80%',
    },
    longText: {
        color: colors.darkGrey,
        fontSize: '80%',
        marginTop: theme.spacing(1)
    },
    bodyField: {
        '& .MuiInputBase-root': {
            borderRadius: '30px'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.lightGreyBorder ?? colors.mediumGrey
        },
        '& .MuiFormLabel-root': {
            color: colors.mediumDarkGrey
        },
        '& .MuiFormLabel-asterisk': {
            display: 'none'
        },
        '& textarea': {
            marginRight: theme.spacing(5)
        },
        '& ::-webkit-scrollbar': {
            width: '4px'
        },
        '& ::-webkit-scrollbar-track': {
            background: 'transparent'
        },
        '& ::-webkit-scrollbar-thumb': {
            background: colors.lightGreyBorder ?? colors.mediumGrey,
            borderRadius: '4px'
        }
    },
    sendMessage: {
        position: 'relative'
    },
    buttonContainer: {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        '&:disabled svg': {
            fill: colors.mediumGrey
        }
    }
}));

const MessageEditor = ({ actor, predefinedTexts, onSubmit }) => {
    const classes = useStyles();
    const authenticationContext = useSelector(state => state.authentication.context);
    const [messageConsentLongTextVisible, setMessageConsentLongTextVisible] = useState(false);
    const bodyContainerRef = useRef(null);

    const { appContext } = useAppContext();

    const initialValues = {
        body: '',
        consentGiven: authenticationContext.isSuperAdminOrImpersonated,
        sendAsAdmin: 'false',
        alsoSendSms: false
    };

    // must use strings as values
    const sendAsAdminItems = [
        { label: strings.formatString(strings.sendingMessageAsUser, actor.name), value: 'false' },
        { label: strings.sendingMessageAsAdmin, value: 'true' }
    ];

    const handleReadMoreClick = (e) => {
        e.preventDefault();
        setMessageConsentLongTextVisible(!messageConsentLongTextVisible);
    };

    const onFormSubmit = formValues => {
        onSubmit({
            ...formValues,
            sendAsAdmin: formValues.sendAsAdmin === 'true' // string -> bool
        });
    };

    const handlePredefinedTextSelect = (predefinedText, form) => {
        insertPredefinedText({
            containerRef: bodyContainerRef,
            fieldName: 'body',
            form,
            predefinedText
        });
    };

    return (
        <Form
            initialValues={initialValues}
            onSubmit={onFormSubmit}
        >
            {({ form, values, invalid, handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit}>
                    <Grid container>
                        {
                            authenticationContext.isSuperAdminOrImpersonated &&
                            (
                                <Grid item xs={12}>
                                    <Radios
                                        name="sendAsAdmin"
                                        data={sendAsAdminItems}
                                    />
                                </Grid>
                            )
                        }
                        {
                            authenticationContext.isSuperAdminOrImpersonated &&
                            (
                                <Grid item xs={12}>
                                    <Checkboxes
                                        name="sendSms"
                                        data={
                                            { label: strings.alsoNotifyBySms, value: true }
                                        }
                                    />
                                </Grid>
                            )
                        }
                        <Grid item xs={12} className={classes.sendMessage} ref={bodyContainerRef}>
                            <TextField
                                className={classes.bodyField}
                                name="body"
                                label={strings.newMessagePlaceholder}
                                variant="outlined"
                                showError={() => { return false; }}
                                required
                                fieldProps={{ validate: required }}
                                multiline
                                maxRows={4}
                            />
                             <IconButton
                                className={classes.buttonContainer}
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={submitting || invalid || !values.consentGiven}
                            >
                                <IconSendMessage fill={appContext.colors.primaryColor}/>
                            </IconButton>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            {
                                values.body && !authenticationContext.isSuperAdminOrImpersonated &&
                                (
                                    <>
                                        <Box>
                                            <Checkboxes
                                                name="consentGiven"
                                                data={{
                                                    label: (
                                                        <span className={classes.shortText}>
                                                            {strings.messageConsent.shortText}
                                                            {' '}
                                                            <a href={void(0)} onClick={handleReadMoreClick}>{strings.messageConsent.readMore}</a>
                                                        </span>
                                                    ),
                                                    value: true
                                                }}
                                            />
                                        </Box>
                                        {
                                            messageConsentLongTextVisible && <Text className={classes.longText} html={strings.messageConsent.longText}/>
                                        }
                                    </>
                                )
                            }
                        </Grid>
                        {
                            authenticationContext.isSuperAdminOrImpersonated && predefinedTexts &&
                            (
                                <PredefinedTextSelector
                                    predefinedTexts={predefinedTexts}
                                    label={strings.insertPredefinedText}
                                    onSelect={predefinedText => handlePredefinedTextSelect(predefinedText, form)}
                                />
                            )
                        }
                    </Grid>
                </form>
            )}
        </Form>
    );
};

MessageEditor.propTypes = {
    actor: PropTypes.object.isRequired,
    predefinedTexts: PropTypes.array,
    onSubmit: PropTypes.func.isRequired
};

export default MessageEditor;
